import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { Visibility, Refresh, RateReview } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient, API_ENDPOINTS } from '../../config/api';

// ─── Design tokens ────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ────────────────────────────────────────────────────────
interface ProfileReviewListItem {
  id: number;
  userId: number;
  userNickname?: string;
  fieldTypes: string[];
  requestedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface ProfileReviewListResponse {
  result: {
    content: ProfileReviewListItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  };
  resultType: string;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  PHOTOS:    '사진',
  JOB:       '직업',
  ONE_LINER: '한줄소개',
  SELF_INTRO:'자기소개',
};

const PAGE_SIZE = 20;

// ─── Component ────────────────────────────────────────────────────
export default function AdminProfileReviewsPage() {
  const navigate = useNavigate();

  const [items, setItems]       = useState<ProfileReviewListItem[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [page, setPage]         = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const fetchList = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<ProfileReviewListResponse>(
        `${API_ENDPOINTS.ADMIN.PROFILE_REVIEWS.LIST}?page=${p}&size=${PAGE_SIZE}`
      );
      if (res.resultType !== 'SUCCESS') throw new Error('목록 조회 실패');
      setItems(res.result?.content ?? []);
      setTotalPages(res.result?.totalPages ?? 0);
      setTotalElements(res.result?.totalElements ?? 0);
      setPage(res.result?.number ?? 0);
    } catch (e: any) {
      setError(e.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(0); }, [fetchList]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px',
              background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RateReview sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>프로필 심사</Typography>
              <Typography variant="body2" color="text.secondary">
                총 {totalElements}건 심사 대기 중
              </Typography>
            </Box>
          </Box>
          <Tooltip title="새로고침">
            <IconButton onClick={() => fetchList(page)} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Error */}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}

        {/* Table */}
        <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F4FF' }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>사용자 ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>수정 필드</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>요청 일시</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      심사 대기 중인 항목이 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#FDF4FF' } }}
                      onClick={() => navigate(`/admin/profile-reviews/${item.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>#{item.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.userId}</Typography>
                        {item.userNickname && (
                          <Typography variant="caption" color="text.secondary">{item.userNickname}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(item.fieldTypes ?? []).map((ft) => (
                            <Chip
                              key={ft}
                              label={FIELD_TYPE_LABELS[ft] ?? ft}
                              size="small"
                              sx={{ bgcolor: '#FFF3E0', color: '#BF360C', fontWeight: 600, fontSize: '0.72rem' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(item.requestedAt)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="심사하기">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/profile-reviews/${item.id}`); }}
                            sx={{ color: PINK }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={page === 0 || loading}
              onClick={() => fetchList(page - 1)}
            >
              이전
            </Button>
            <Typography sx={{ alignSelf: 'center', fontSize: '0.9rem' }}>
              {page + 1} / {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={page >= totalPages - 1 || loading}
              onClick={() => fetchList(page + 1)}
            >
              다음
            </Button>
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
}
